import React, { useEffect, useState } from "react";
import SiteLayout from "@/components/SiteLayout";
import api from "@/lib/api";
import { fetchPlans, FALLBACK_PLANS } from "@/lib/plans";
import useScrollReveal from "@/hooks/useScrollReveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import HeroBadge from "@/components/HeroBadge";
import { inr } from "@/lib/format";
import { Sparkles, MessageCircle, ArrowRight, ShieldCheck, User, Users, Heart, Info } from "lucide-react";

const FAQS = [
  ["What is the Nirmal Privilege Card (NPC)?", "The Nirmal Privilege Card (NPC) is a healthcare privilege and patient support program offered by Nirmal Group of Hospitals. It is designed to provide eligible members and their families with easier access to unlimited OP consultations (selected specialities), awareness programs, and selected hospital benefits."],
  ["Is NPC a health insurance or mediclaim policy?", "No. NPC is not an insurance product, mediclaim policy, cashless scheme, or financial instrument. It is a healthcare privilege program that offers access to selected healthcare-related benefits and services subject to hospital policy and applicable terms."],
  ["Who can enroll in NPC?", "Individuals, couples, families, and organizations may enroll under available membership plans, subject to verification and approval by hospital management."],
  ["What membership plans are available?", "Indicative membership plans: Bronze (Individual), Silver (Couple), Gold (Couple + Children + Parents). Final plan structure, pricing, and benefits may change as per hospital policy."],
  ["What are the key benefits of NPC?", "Unlimited OPD consultations with eligible specialists during OPD hours; special rates on diagnostics & investigations; benefits on preventive health-check packages; pharmacy privileges on eligible in-house purchases; access to wellness initiatives; and family healthcare support under one membership ecosystem."],
  ["Which specialities are covered for unlimited consultations?", "Eligible specialities for unlimited free consultations include General Physician, Orthopedics, Gynecology, Pediatrics, and General Surgery. Selected visiting and super-speciality consultants may be available at privileged or concessional consultation rates."],
  ["Are emergency services covered under NPC?", "No. NPC benefits are not applicable during emergency services, emergency hours, or emergency admissions."],
  ["Can NPC benefits be used on public holidays?", "No. Benefits may not be applicable on public holidays unless specifically approved by hospital management."],
  ["Does NPC provide priority admission or emergency treatment?", "No. Membership does not guarantee admission, treatment, emergency priority, bed availability, ICU availability, or doctor availability."],
  ["Can I avail IPD bill discount if I purchase the NPC membership card during my hospital stay?", "To avail an IPD bill discount, the NPC membership card must be active and issued prior to the date of admission. If the NPC card is purchased during your stay, the membership benefits will be applicable only after the date of discharge and cannot be redeemed against the current admission bill."],
  ["Are diagnostics and pathology services included?", "Members receive special rates on selected pathology and radiology investigations performed within hospital infrastructure, subject to hospital policy."],
  ["Are medicines covered under NPC?", "Eligible benefits apply only on purchases from the in-house hospital pharmacy."],
  ["Can NPC benefits be combined with other offers or insurance?", "No. NPC benefits cannot be combined with insurance claims, TPA billing, government schemes, discounted packages, promotional offers, or other hospital schemes."],
  ["What is NOT included in the NPC program?", "Implants and stents, blood products, outsourced investigations, government taxes/GST, special promotional packages, and services already under discounted pricing are generally not covered."],
  ["Can the hospital change the program structure or pricing?", "Yes. Hospital management reserves the right to revise, modify, suspend, or discontinue any membership plan, benefit, pricing, or policy without prior notice."],
  ["Can the hospital terminate a privilege card holder?", "Yes. Misuse, fraudulent activity, policy violations, misconduct, or non-compliance with hospital rules may lead to suspension or cancellation of membership."],
  ["Is NPC transferable?", "No. Membership is non-transferable and can only be used by registered eligible members covered under the enrolled plan."],
];

export default function FAQ() {
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", interested_plan: "", message: "" });
  const [loading, setLoading] = useState(false);
  useScrollReveal();

  useEffect(() => { let ok = true; fetchPlans().then((p) => { if (ok) setPlans(p); }); return () => { ok = false; }; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads/enquiry", form);
      toast.success("Enquiry submitted. We'll call you back.");
      setForm({ name: "", mobile: "", email: "", interested_plan: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <SiteLayout>
      <section className="hero-grad relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-12 relative">
          <div className="max-w-3xl reveal">
            <HeroBadge>FAQ · <span className="text-[#A53A2A]">Common Questions</span></HeroBadge>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0F2540] leading-[1.04]">
              Questions, <span className="bg-gradient-to-r from-[#A53A2A] via-[#C89B3C] to-[#A53A2A] bg-clip-text text-transparent">answered.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-[#5C6B7A]">Everything you need to know about the Nirmal Privilege Card — plans, billing, benefits and how to use your membership at our Adajan New Building.</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 lg:px-10 py-12">
        <div className="reveal" data-testid="faq-accordion">
          <Accordion type="single" collapsible className="space-y-4">
            {FAQS.map(([q, a], i) => (
              <AccordionItem key={i} value={`i${i}`} className="lux-card rounded-2xl px-6 py-1 border-0">
                <AccordionTrigger className="text-left font-display text-lg text-[#0F2540] hover:no-underline py-5">{q}</AccordionTrigger>
                <AccordionContent className="text-[#5C6B7A] leading-relaxed pb-5">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* NPC Plans — Indicative Membership Structure */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12" data-testid="faq-plans-section">
        <div className="max-w-3xl reveal">
          <span className="overline">NPC plans</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-[#0F2540] leading-tight">
            Indicative <span className="text-[#A53A2A]">membership structure.</span>
          </h2>
          <p className="mt-4 text-[#5C6B7A] text-lg leading-relaxed">
            A snapshot of the membership tiers we offer. Final structure, pricing, and benefits remain subject to management approval.
          </p>
        </div>

        <div className="mt-10 reveal" style={{ animationDelay: "120ms" }}>
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 rounded-t-2xl bg-[#0F2540] text-white text-[10px] uppercase tracking-[0.22em] font-bold">
            <div className="col-span-3">Plan</div>
            <div className="col-span-6">Coverage</div>
            <div className="col-span-3 text-right">Proposed Annual Fee</div>
          </div>
          <div className="md:rounded-b-2xl overflow-hidden ring-1 ring-[#E1E8F0] divide-y divide-[#E1E8F0]">
            {[
              { tier: "Bronze", coverage: "Individual",                 fee: "₹2,000", grad: "from-[#C28B6F] via-[#A8704F] to-[#8B5A3C]", icon: User },
              { tier: "Silver", coverage: "Couple",                     fee: "₹3,000", grad: "from-[#B6C2CC] via-[#8A98A4] to-[#5E6B78]", icon: Users },
              { tier: "Gold",   coverage: "Couple + Children + Parents", fee: "₹6,000", grad: "from-[#E3BC58] via-[#C89B3C] to-[#9C7421]", icon: Heart },
            ].map((p, i) => (
              <div key={p.tier} className="group bg-white hover:bg-[#FAFBFC] transition-colors duration-300" data-testid={`faq-plan-row-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-6 py-5 items-center">
                  <div className="md:col-span-3 flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${p.grad} grid place-items-center text-white shadow-md group-hover:scale-110 transition-transform duration-500`}>
                      <p.icon className="h-5 w-5" />
                    </div>
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-bold text-white bg-gradient-to-r ${p.grad}`}>{p.tier}</div>
                  </div>
                  <div className="md:col-span-6 text-[#0F2540] font-semibold">
                    <span className="md:hidden text-[10px] uppercase tracking-wider text-[#5C6B7A] block">Coverage</span>
                    {p.coverage}
                  </div>
                  <div className="md:col-span-3 md:text-right">
                    <span className="md:hidden text-[10px] uppercase tracking-wider text-[#5C6B7A] block">Proposed Annual Fee</span>
                    <span className="font-display text-2xl font-bold text-[#1E4F87]">{p.fee}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 flex items-start gap-2.5 text-xs text-[#5C6B7A] italic">
            <Info className="h-4 w-4 text-[#C89B3C] flex-shrink-0 mt-0.5 not-italic" />
            <span>Final pricing, structure, and benefits remain subject to management approval.</span>
          </div>
        </div>
      </section>

      {/* Plan enquiry */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16" data-testid="plan-enquiry-section">
        <div className="rounded-[2rem] mesh-dark p-8 lg:p-14 relative overflow-hidden reveal">
          <div className="absolute inset-0 dot-grid opacity-15 pointer-events-none" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="overline text-[#C89B3C]">Plan enquiry</span>
              <h2 className="mt-3 font-display text-4xl lg:text-5xl font-bold text-white leading-tight">Not sure which plan suits you?</h2>
              <p className="mt-4 text-white/80 text-lg">Tell us a bit about your family and our advisor will recommend the right tier — usually within one business day.</p>
              <div className="mt-6 flex items-center gap-3 text-sm text-white/70">
                <ShieldCheck className="h-5 w-5 text-[#C89B3C]" /> No spam. Just one helpful call.
              </div>
            </div>
            <form onSubmit={submit} className="rounded-2xl bg-white p-6 lg:p-7 space-y-3 shadow-2xl" data-testid="enquiry-form">
              <Inp label="Name" v={form.name} s={(v) => setForm({ ...form, name: v })} required testid="e-name" />
              <div className="grid sm:grid-cols-2 gap-3">
                <Inp label="Mobile" v={form.mobile} s={(v) => setForm({ ...form, mobile: v })} required testid="e-mobile" />
                <Inp label="Email" type="email" v={form.email} s={(v) => setForm({ ...form, email: v })} required testid="e-email" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F2540]">Interested Plan</label>
                <select value={form.interested_plan} onChange={(e) => setForm({ ...form, interested_plan: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]" data-testid="e-plan">
                  <option value="">Select a plan…</option>
                  {plans.map((p) => <option key={p.plan_id} value={p.name}>{p.name} — {inr(p.price)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F2540]">Message</label>
                <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]" data-testid="e-message" />
              </div>
              <button disabled={loading} className="btn-primary w-full justify-center" data-testid="enquiry-submit-btn">
                {loading ? "Submitting…" : <>Request callback <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function Inp({ label, v, s, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-[#0F2540]">{label}{required && " *"}</label>
      <input type={type} value={v} required={required} onChange={(e) => s(e.target.value)}
        className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
        data-testid={testid} />
    </div>
  );
}
